/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback, useRef} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik, FormikProvider, FieldArray} from 'formik'

import {KTSVG, PurchaseRequestStatus} from '../../../helpers'
import {KTCard, KTCardBody, Select, SelectCreatable, Loading, Datepicker} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  allCompanyLookup,
  projectLookup,
  allProductLookup,
  basicLookup,
  allUserLookup,
} from '../../../redux/slices/lookup.slice'
import {purchaseRequestGetId, purchaseRequestUpdate} from '../../../redux/slices/purchase.slice'
import {productGetInfo, productCreateFast} from '../../../redux/slices/product.slice'

import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  productLookup: state.lookup.productLookup,
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  ProductUnit: state.lookup.ProductUnit,
  allUserLookupList: state.lookup.allUserLookupList,
  loading: state.lookup.loading
})

const selectProductData = (state) => ({
  detail: state.product.detail,
  isRedirect: state.product.isRedirect
})

const selectPurchaseData = (state) => ({
  detail: state.purchase.detail,
  isRedirect: state.purchase.isRedirect,
  loading: state.purchase.loading
})

function Edit() {
  const {id} = useParams()
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  
  const pageUrl = useMemoizedPageUrl(location)

  const {productLookup, projectLookupList, companyLookup, ProductUnit, allUserLookupList, loading} =
    useSelector(selectLookupData)
  const {detail: product, isRedirect: createProduct} = useSelector(selectProductData)
  const {detail, isRedirect, loading: updateLoading} = useSelector(selectPurchaseData)
  const dispatch = useDispatch()
  const [changeProduct, setChangeProduct] = useState()
  const [createItem, setCreateItem] = useState()

  const formikRef = useRef()
  const isFormPopulatedRef = useRef(false)

  useEffect(() => {
    dispatch(allCompanyLookup())
    dispatch(projectLookup())
    dispatch(allProductLookup())
    dispatch(basicLookup('ProductUnit'))
    dispatch(allUserLookup())
    dispatch(purchaseRequestGetId(id))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    PurchaseRequestStatusId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Id: id,
    Name: '',
    ProjectId: null,
    CompanyId: null,
    EditDate: new Date(),
    DueDate: new Date(),
    PurchaseRequestStatusId: '',
    Note: '',
    RelatedUserId: null,
    File: null,
    PurchaseRequestItems: [
      {
        Id: 0,
        ProductId: null,
        Amount: 0,
        ProductUnitId: 1,
        Description: '',
      },
    ],
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(purchaseRequestUpdate(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    formikRef.current = formik
  }, [formik])

  useEffect(() => {
    if (detail && formikRef.current && !isFormPopulatedRef.current) {
      Object.keys(formValues).forEach(key => {
        const detailKey = key.charAt(0).toLowerCase() + key.slice(1)
        if (detailKey in detail) {
          if (detailKey === 'purchaseRequestItems') {
            detail[detailKey].forEach((item, index) => {
              Object.keys(item).forEach(invoice => {
                const propValArr = invoice.charAt(0).toUpperCase() + invoice.slice(1)
                formikRef.current.setFieldValue(`PurchaseRequestItems[${index}][${propValArr}]`, item[invoice])
              })
            })
          } else {
            formikRef.current.setFieldValue(key, detail[detailKey])
          }
        }
      })
      isFormPopulatedRef.current = true
    }
  }, [detail, formValues])

  useEffect(() => {
    if (isRedirect) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
    }
  }, [isRedirect, navigate, pageUrl, id])

  const onChangeItem = useCallback((target, value, index) => {
    formik.setFieldValue(`PurchaseRequestItems[${index}][${target}]`, value)
  }, [formik])

  const onClickNewItem = useCallback((arrayHelpers) => {
    arrayHelpers.push({
      Id: 0,
      ProductId: null,
      Amount: 0,
      ProductUnitId: 1,
      Description: '',
    })
  }, [])

  const onChangeProduct = useCallback((item, index) => {
    formik.setFieldValue(`PurchaseRequestItems[${index}].ProductId`, Number(item?.value))
    setChangeProduct(index)
    dispatch(productGetInfo({Id: item?.value}))
  }, [dispatch])

  useEffect(() => {
    if (product)
      formik.setFieldValue(
        `PurchaseRequestItems[${changeProduct}].ProductUnitId`,
        product?.productUnitId
      )
  }, [product])

  const onClickFastProduct = useCallback((Name, Index) => {
    dispatch(productCreateFast({Name}))
    setCreateItem({Name, Index})
  }, [dispatch])

  useEffect(() => {
    if (createProduct) dispatch(allProductLookup())
  }, [createProduct, dispatch])

  useEffect(() => {
    if (productLookup && createProduct) {
      formik.setFieldValue(`PurchaseRequestItems[${createItem?.Index}].ProductId`, createProduct.id)
    }
  }, [productLookup, createProduct])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.REQUEST'}),
      path: '/purchase/request',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({id: 'BC.REQUEST.EDIT'})}</PageTitle>
      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-12'>
          <KTCard>
            <KTCardBody>
              <FormikProvider value={formik}>
                <form className='form' onSubmit={formik.handleSubmit} noValidate>
                  <div className='row mb-6'>
                    <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'REQUEST.CREATE.NAME'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        required
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('Name')}
                      />
                      {formik.touched.Name && formik.errors.Name && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.Name}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'REQUEST.CREATE.PROJECTID'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={projectLookupList}
                        required
                        {...formik.getFieldProps('ProjectId')}
                        onChange={(item) => formik.setFieldValue('ProjectId', Number(item?.value))}
                      />
                      {formik.touched.ProjectId && formik.errors.ProjectId && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.ProjectId}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'REQUEST.CREATE.PURCHASEREQUESTSTATUSID'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={PurchaseRequestStatus}
                        required
                        {...formik.getFieldProps('PurchaseRequestStatusId')}
                        onChange={(item) =>
                          formik.setFieldValue('PurchaseRequestStatusId', Number(item?.value))
                        }
                        textType={'intl'}
                      />
                      {formik.touched.PurchaseRequestStatusId &&
                        formik.errors.PurchaseRequestStatusId && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.PurchaseRequestStatusId}</span>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'REQUEST.CREATE.COMPANYID'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={companyLookup}
                        {...formik.getFieldProps('CompanyId')}
                        onChange={(item) => formik.setFieldValue('CompanyId', Number(item?.value))}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'REQUEST.CREATE.EDITDATE'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Datepicker
                        {...formik.getFieldProps('EditDate')}
                        onChange={([date]) => formik.setFieldValue('EditDate', date)}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'REQUEST.CREATE.DUEDATE'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Datepicker
                        {...formik.getFieldProps('DueDate')}
                        onChange={([date]) => formik.setFieldValue('DueDate', date)}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'REQUEST.CREATE.RELATEDSTAFFID'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={allUserLookupList}
                        required
                        {...formik.getFieldProps('RelatedUserId')}
                        onChange={(item) =>
                          formik.setFieldValue('RelatedUserId', Number(item?.value))
                        }
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'REQUEST.CREATE.NOTE'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <textarea
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        disabled={formik.isSubmitting}
                        rows={4}
                        {...formik.getFieldProps('Note')}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'REQUEST.CREATE.FILE'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <label
                        htmlFor='File'
                        className='btn btn-outline btn-outline-dashed btn-outline-dark btn-active-light-dark position-relative overflow-hidden'
                      >
                        <input
                          className='position-absolute z-index-2 opacity-0 w-1px h-1px'
                          type='file'
                          accept='image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.kmz,.kml,.ncz,.dwg,.kmz,.kml,.ncz,.dwg'
                          id={'File'}
                          name={'File'}
                          onChange={(event) =>
                            formik.setFieldValue('File', event.currentTarget.files[0])
                          }
                        />
                        <KTSVG
                          path='/images/icons/duotune/communication/com008.svg'
                          className='svg-icon-3 position-relative'
                        />
                        {formik.values.File
                          ? formik.values.File.name
                          : intl.formatMessage({id: 'REQUEST.CREATE.FILE'})}
                      </label>
                    </div>
                  </div>
                  <FieldArray
                    name='PurchaseRequestItems'
                    render={(arrayHelpers) => (
                      <>
                        <div className='table-responsive'>
                          <table className='table g-5 gs-0 mb-0 fw-bold text-gray-700'>
                            <thead>
                              <tr className='border-bottom fs-7 fw-bold text-gray-700 text-uppercase'>
                                <th>
                                  {intl.formatMessage({
                                    id: 'REQUEST.CREATE.PURCHASEREQUESTITEMS.PRODUCTID',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'REQUEST.CREATE.PURCHASEREQUESTITEMS.AMOUNT',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'REQUEST.CREATE.PURCHASEREQUESTITEMS.PRODUCTUNITID',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'REQUEST.CREATE.PURCHASEREQUESTITEMS.DESCRIPTION',
                                  })}
                                </th>
                                <th>&nbsp;</th>
                              </tr>
                            </thead>
                            <tbody>
                              {formik.values.PurchaseRequestItems.map((item, index) => (
                                <tr className='border-bottom border-bottom-dashed' key={index}>
                                  <td className='pe-7 ps-1'>
                                    <SelectCreatable
                                      data={productLookup}
                                      name={`PurchaseRequestItems[${index}].ProductId`}
                                      value={formik.values.PurchaseRequestItems[index].ProductId}
                                      required
                                      onChange={(item) => onChangeProduct(item, index)}
                                      onCreate={(item) => onClickFastProduct(item, index)}
                                    />
                                  </td>

                                  <td className='ps-0'>
                                    <input
                                      type='number'
                                      className='form-control form-control-solid'
                                      autoComplete='off'
                                      disabled={formik.isSubmitting}
                                      placeholder={intl.formatMessage({
                                        id: 'REQUEST.CREATE.PURCHASEREQUESTITEMS.AMOUNT',
                                      })}
                                      name={`PurchaseRequestItems[${index}].Amount`}
                                      value={formik.values.PurchaseRequestItems[index].Amount}
                                      required
                                      onChange={(event) =>
                                        onChangeItem('Amount', Number(event.target.value), index)
                                      }
                                      onFocus={(event) => event.target.select()}
                                    />
                                  </td>

                                  <td className='ps-0'>
                                    <Select
                                      data={ProductUnit}
                                      name={`PurchaseRequestItems[${index}].ProductUnitId`}
                                      value={
                                        formik.values.PurchaseRequestItems[index].ProductUnitId
                                      }
                                      required
                                      onChange={(item) =>
                                        onChangeItem('ProductUnitId', Number(item?.value), index)
                                      }
                                    />
                                  </td>

                                  <td className='ps-0'>
                                    <input
                                      type='text'
                                      className='form-control form-control-solid'
                                      autoComplete='off'
                                      disabled={formik.isSubmitting}
                                      placeholder={intl.formatMessage({
                                        id: 'REQUEST.CREATE.PURCHASEREQUESTITEMS.DESCRIPTION',
                                      })}
                                      name={`PurchaseRequestItems[${index}].Description`}
                                      value={formik.values.PurchaseRequestItems[index].Description}
                                      onChange={formik.handleChange}
                                    />
                                  </td>

                                  <td className='pt-5 text-end'>
                                    <div className='d-flex'>
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-icon btn-active-color-primary'
                                        onClick={() => arrayHelpers.remove(index)}
                                      >
                                        <KTSVG
                                          path='/images/icons/duotune/general/gen027.svg'
                                          className='svg-icon-2'
                                        />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className='border-top border-top-dashed align-middle fs-6 fw-bold text-gray-700'>
                                <th colSpan={4}>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-outline btn-outline-dashed btn-outline-success btn-active-light-success'
                                    onClick={() => onClickNewItem(arrayHelpers)}
                                  >
                                    <KTSVG
                                      path='/images/icons/duotune/arrows/arr009.svg'
                                      className='svg-icon-2'
                                    />
                                    {intl.formatMessage({id: 'ADD_NEW_LINE'})}
                                  </button>
                                </th>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </>
                    )}
                  />

                  <div className='row pt-15'>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                      <button
                        className='btn btn-light'
                        onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)}
                      >
                        {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                      </button>
                      <button
                        type='submit'
                        className='btn btn-primary'
                        data-kt-users-modal-action='submit'
                        disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                      >
                        <span className='indicator-label'>
                          {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                        </span>
                        {formik.isSubmitting && (
                          <span className='indicator-progress'>
                            {intl.formatMessage({id: 'PLEASE'})}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </FormikProvider>
            </KTCardBody>
          </KTCard>
        </div>
      </div>

      {(loading | updateLoading) > 0 && <Loading />}
    </>
  )
}
export {Edit}
